import { Box, Button, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { canManageBackgroundImages } from "@shared/config/background-image-config";
import { remult } from "remult";
import { type FC, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { useUser } from "../../contexts/UserProvider";
import { useAlert } from "../../contexts/AlertProvider";
import useQuestion from "../../dialog-components/QuestionDialog";
import { getErrorMessage } from "../auth/Auth";
import { BackgroundImageManager } from "../../components/organization-background-images/BackgroundImageManager";
import { DeleteBackgroundImageDialog } from "../../components/organization-background-images/DeleteBackgroundImageDialog";
import {
  deleteUnusedOrganizationBackgroundImages,
  organizationBackgroundImagesQueryKey,
} from "../../components/organization-background-images/imageGalleryApi";
import { useOrganizationBackgroundImages } from "../../components/organization-background-images/useOrganizationBackgroundImages";

type Props = {
  headerOpacity: number;
};

export const OrganizationBackgroundGallerySettings: FC<Props> = ({
  headerOpacity,
}) => {
  const { currOrganization } = useOrganization();
  const { currentUser } = useUser();
  const canManage = canManageBackgroundImages(
    currentUser?.phoneNumber || remult.user?.phoneNumber,
    remult.user?.roles,
  );
  const { setAlertText, setSeverity } = useAlert();
  const question = useQuestion();
  const queryClient = useQueryClient();
  const [imageToDelete, setImageToDelete] = useState<string | null>(null);

  const {
    data: backgroundImages,
    isLoading,
    refetch,
  } = useOrganizationBackgroundImages(currOrganization?.id);

  const unusedCount = backgroundImages?.filter((image) => image.isUnused).length ?? 0;

  const deleteUnusedMutation = useMutation({
    mutationFn: () =>
      deleteUnusedOrganizationBackgroundImages(currOrganization!.id),
    onSuccess: (deletedCount) => {
      void queryClient.invalidateQueries({
        queryKey: organizationBackgroundImagesQueryKey(currOrganization?.id),
      });
      setAlertText(
        deletedCount > 0
          ? `נמחקו ${deletedCount} תמונות שלא בשימוש`
          : "אין תמונות למחיקה",
      );
      setSeverity("success");
    },
    onError: (error) => {
      setAlertText(getErrorMessage(error));
      setSeverity("error");
    },
  });

  async function handleDeleteUnused() {
    const confirmed = await question(
      "מחיקת תמונות שלא בשימוש",
      `למחוק ${unusedCount} תמונות שלא מקושרות למשימות?`,
      { confirmText: "מחיקה", cancelText: "ביטול" },
    );
    if (confirmed) deleteUnusedMutation.mutate();
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 4,
          border: "1px solid rgba(255, 255, 255, 0.2)",
          background: "rgba(255, 255, 255, 0.9)",
          backdropFilter: "blur(10px)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            background: `linear-gradient(135deg, rgba(33, 150, 243, ${headerOpacity}) 0%, rgba(25, 118, 210, ${headerOpacity}) 100%)`,
            p: 3,
            color: "white",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.2rem" }}>
            גלריית תמונות רקע להתנדבויות
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              mt: 1,
              fontSize: "0.95rem",
            }}
          >
            לוחצים על תמונה כדי לערוך שיוך או למחוק. משימה סגורה או בארכיון לא
            נחשבת שימוש.
          </Typography>
        </Box>
        <Box sx={{ p: 4 }} className="organization-background-gallery">
          {isLoading ? (
            <Stack alignItems="center" py={4}>
              <CircularProgress />
            </Stack>
          ) : backgroundImages?.length ? (
            <Stack gap={2}>
              {canManage && unusedCount > 0 && (
                <Button
                  color="error"
                  variant="outlined"
                  sx={{ alignSelf: "flex-start" }}
                  disabled={deleteUnusedMutation.isPending}
                  onClick={() => void handleDeleteUnused()}
                >
                  {deleteUnusedMutation.isPending
                    ? "מוחק..."
                    : `מחיקת ${unusedCount} תמונות שלא בשימוש`}
                </Button>
              )}
              {canManage ? (
                <BackgroundImageManager
                  organizationId={currOrganization?.id}
                  images={backgroundImages}
                  onDelete={setImageToDelete}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  עריכת השיוך זמינה למנהלים המורשים בלבד.
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              אין תמונות רקע בגלריה. ניתן להעלות תמונות בעת יצירת משימה.
            </Typography>
          )}
        </Box>
      </Paper>
      <DeleteBackgroundImageDialog
        open={!!imageToDelete}
        imageId={imageToDelete}
        organizationId={currOrganization?.id}
        onClose={() => setImageToDelete(null)}
        onDeleted={() => {
          void refetch();
        }}
      />
    </>
  );
};
